import React, { useEffect, useMemo, useState } from 'react';
import { runnerApi, errorText, type GridMigration, type GridTarget, type PreviewResult } from '../../../api/migrationRunner';
import { Btn, ErrorBox, Panel, Pill, Spinner, shortName } from './ui';

interface Props {
  migration: GridMigration;
  /** Databases of this migration's scope, with whether it still needs to run there. */
  targets: (GridTarget & { pending: boolean })[];
  onRun: (targets: string[]) => void;
}

const OUTCOME: Record<string, { tone: 'ok' | 'mut' | 'bad' | 'warn'; label: string }> = {
  apply: { tone: 'ok', label: 'will apply' },
  noop: { tone: 'mut', label: 'nothing to do' },
  fail: { tone: 'bad', label: 'would fail' },
  unknown: { tone: 'warn', label: 'not checked' },
};

/** Preview a migration: lint + a real dry run (information_schema) per database. */
const PreviewPanel: React.FC<Props> = ({ migration, targets, onRun }) => {
  const pending = useMemo(() => targets.filter((t) => t.pending && t.exists !== false), [targets]);
  const [chosen, setChosen] = useState<string[]>(() => pending.slice(0, 5).map((t) => t.targetDb));
  const [result, setResult] = useState<PreviewResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (dbs: string[]) => {
    setLoading(true);
    setError(null);
    try {
      setResult(await runnerApi.preview({ migrationId: migration.scriptId, targets: dbs }));
    } catch (err) {
      setError(errorText(err, 'Preview failed'));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { run(chosen); }, [migration.scriptId]);

  const nonIdem = result?.statements.filter((s) => !s.idempotent) ?? [];
  const fileErrors = result?.fileIssues.filter((i) => i.severity === 'error' && i.rule !== 'non-idempotent') ?? [];
  const fileWarns = result?.fileIssues.filter((i) => i.severity === 'warn' && i.rule !== 'non-idempotent') ?? [];
  const nameOf = (db: string) => targets.find((t) => t.targetDb === db)?.name ?? db;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <b className="text-[15px] text-slate-900">{shortName(migration.folderPath)}</b>
        <Pill tone={migration.scope === 'registry' ? 'vio' : 'info'}>{migration.scope}{migration.scopeInferred ? ' (guessed)' : ''}</Pill>
        <Pill tone="mut">v{migration.version}</Pill>
        {result && (nonIdem.length === 0 && fileErrors.length === 0
          ? <Pill tone="ok">checks passed</Pill>
          : <Pill tone={result.blocked || fileErrors.length ? 'bad' : 'warn'}>{nonIdem.length + fileErrors.length} problem{nonIdem.length + fileErrors.length === 1 ? '' : 's'}</Pill>)}
        <span className="flex-1" />
        <Btn disabled={pending.length === 0} onClick={() => onRun(pending.map((t) => t.targetDb))}>
          Run on {pending.length} not-run database{pending.length === 1 ? '' : 's'}…
        </Btn>
      </div>

      {error && <ErrorBox>{error}</ErrorBox>}
      {loading && !result && <div className="flex items-center gap-2 text-sm text-slate-500"><Spinner /> Checking…</div>}

      {result && (
        <div className="grid gap-4 lg:grid-cols-[1.3fr_1fr]">
          <Panel title="What it will do" bodyClass="max-h-[520px] divide-y divide-slate-100 overflow-auto">
            {result.statements.map((s) => (
              <div key={s.index} className="grid grid-cols-[28px_1fr_auto] items-start gap-2 px-3 py-2">
                <span className="pt-0.5 text-right font-mono text-xs font-bold text-slate-400">{s.index + 1}</span>
                <div className="min-w-0">
                  <div className="text-[12.5px] font-semibold text-slate-800">{s.summary}</div>
                  <pre className="mt-0.5 max-h-24 overflow-hidden whitespace-pre-wrap break-words font-mono text-[11px] text-slate-500">{s.sql}</pre>
                  {s.issue && <div className="mt-1 text-[12px] text-red-700">{s.issue} Fix: add IF NOT EXISTS / IF EXISTS.</div>}
                </div>
                {s.idempotent ? <Pill tone="ok">safe to repeat</Pill> : <Pill tone="bad">can't run twice</Pill>}
              </div>
            ))}
          </Panel>

          <div className="space-y-4">
            <Panel title="Checks">
              <div className="space-y-2 text-[13px]">
                <Lint tone={nonIdem.length ? (result.blocked ? 'bad' : 'warn') : 'ok'} label={nonIdem.length ? (result.blocked ? 'Blocks' : 'Warns') : 'OK'}>
                  {nonIdem.length
                    ? `${nonIdem.length} statement${nonIdem.length === 1 ? '' : 's'} can't run twice${result.blocked ? ' — runs are blocked until fixed or overridden (Settings).' : '.'}`
                    : 'Every statement can run twice without failing.'}
                </Lint>
                <Lint tone={migration.scopeInferred ? 'warn' : 'ok'} label={migration.scopeInferred ? 'Warns' : 'OK'}>
                  {migration.scopeInferred ? 'No @scope header: treated as tenant.' : `Scope header present: ${migration.scope}.`}
                </Lint>
                {fileErrors.map((i) => <Lint key={i.rule} tone="bad" label="Fix">{i.message}</Lint>)}
                {fileWarns.filter((i) => i.rule !== 'missing-scope').map((i) => <Lint key={i.rule} tone="warn" label="Warns">{i.message}</Lint>)}
                {result.summary.hasDestructive && <Lint tone="warn" label="Note">Drops or deletes data. Make sure a backup is taken first.</Lint>}
                <Lint tone="mut" label="Note">Schema changes can't be undone automatically once applied. Batches back up first unless you turn it off.</Lint>
              </div>
            </Panel>

            <Panel
              title="Real dry run"
              actions={<Btn kind="ghost" small disabled={loading || chosen.length === 0} onClick={() => run(chosen)}>{loading ? 'Checking…' : 'Re-check'}</Btn>}
            >
              <div className="mb-3 flex flex-wrap gap-1.5">
                {targets.filter((t) => t.exists !== false).map((t) => {
                  const on = chosen.includes(t.targetDb);
                  return (
                    <button
                      type="button"
                      key={t.targetDb}
                      onClick={() => setChosen(on ? chosen.filter((x) => x !== t.targetDb) : [...chosen, t.targetDb].slice(0, 20))}
                      className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${on ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-slate-300 text-slate-500'}`}
                    >
                      {t.name}{t.pending ? '' : ' (applied)'}
                    </button>
                  );
                })}
              </div>
              <div className="space-y-3">
                {Object.entries(result.previews).map(([db, p]) => (
                  <div key={db} className="rounded-lg border border-slate-200 p-2.5">
                    <div className="mb-1 flex items-center gap-2 text-[13px]">
                      <b className="text-slate-900">{nameOf(db)}</b>
                      {p?.summary && (
                        <span className="text-xs text-slate-500">
                          {p.summary.apply} apply · {p.summary.noop} nothing to do · {p.summary.fail} would fail
                        </span>
                      )}
                    </div>
                    {p?.error && <div className="text-xs text-red-700">{p.error}</div>}
                    {p?.predictions?.filter((x) => x.outcome !== 'apply' || x.rows !== null).map((x) => (
                      <div key={x.index} className="flex items-start gap-2 text-[12px]">
                        <span className="w-6 shrink-0 text-right font-mono text-slate-400">{x.index + 1}</span>
                        <Pill tone={OUTCOME[x.outcome].tone}>{OUTCOME[x.outcome].label}</Pill>
                        <span className="text-slate-600">{x.notes.join('; ')}</span>
                      </div>
                    ))}
                    {p?.predictions && p.predictions.every((x) => x.outcome === 'apply' && x.rows === null) && (
                      <div className="text-xs text-green-700">Every statement would apply.</div>
                    )}
                  </div>
                ))}
                {Object.keys(result.previews).length === 0 && <div className="text-xs text-slate-500">Pick databases above to dry-run against.</div>}
              </div>
            </Panel>
          </div>
        </div>
      )}
    </div>
  );
};

const Lint: React.FC<{ tone: 'ok' | 'bad' | 'warn' | 'mut' | 'info'; label: string; children: React.ReactNode }> = ({ tone, label, children }) => (
  <div className="flex items-start gap-2">
    <span className="mt-0.5 shrink-0"><Pill tone={tone}>{label}</Pill></span>
    <span className="text-slate-700">{children}</span>
  </div>
);

export { Lint };
export default PreviewPanel;
